import { styled } from "goober";
import type { ReactNode } from "react";
import { Button } from "./Button";
import { Icon, type IconName } from "./Icon";
import { Modal } from "./Modal";
import { TabButton, TabLinkButton } from "./TabButton";
import { Text } from "./Text";

const Header = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
`;

const HeaderLeft = styled("div")`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Tabs = styled("div")`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`;

const HeaderRight = styled("div")`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

const Body = styled("div")`
  padding: 16px;
  flex: 1;
  border: 0.5px solid var(--border-alpha);
  border-left: 0;
  border-right: 0;
  overflow: auto;
`;

const Footer = styled("div")`
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export type ModalTab<K extends string> = {
  key: K;
  label: string;
  icon: IconName;
  disabled?: boolean;
  to?: string;
};

export function TabbedModal<K extends string>({
  icon,
  title,
  titleAfter,
  headerRight,
  tabs,
  activeTab,
  onTabChange,
  onClose,
  footerLeft,
  footerRight,
  width = 680,
  height = 760,
  children,
}: {
  icon: ReactNode;
  title: ReactNode;
  titleAfter?: ReactNode;
  headerRight?: ReactNode;
  tabs: readonly ModalTab<K>[];
  activeTab: K;
  onTabChange: (key: K) => void;
  onClose: () => void;
  footerLeft?: ReactNode;
  footerRight?: ReactNode;
  width?: number;
  height?: number;
  children: ReactNode;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      style={{ width, height, display: "flex", flexDirection: "column" }}
    >
      <Header>
        <HeaderLeft>
          {icon}
          <Text variant="text-regular-md" ellipsis>
            {title}
          </Text>
          {titleAfter}
        </HeaderLeft>
        <Tabs>
          {tabs.map((tab) =>
            tab.to && !tab.disabled ? (
              <TabLinkButton
                key={tab.key}
                to={tab.to}
                className={activeTab === tab.key ? "active" : undefined}
              >
                <Icon name={tab.icon} size={16} />
                <Text variant="text-small-md">{tab.label}</Text>
              </TabLinkButton>
            ) : (
              <TabButton
                key={tab.key}
                className={activeTab === tab.key ? "active" : undefined}
                disabled={tab.disabled}
                onPress={() => onTabChange(tab.key)}
              >
                <Icon name={tab.icon} size={16} />
                <Text variant="text-small-md">{tab.label}</Text>
              </TabButton>
            )
          )}
        </Tabs>
        <HeaderRight>
          {headerRight}
          <Button size="l" variant="secondary" icon="x" onClick={onClose} />
        </HeaderRight>
      </Header>
      <Body>{children}</Body>
      <Footer>
        {footerLeft ?? <div />}
        {footerRight ?? <div />}
      </Footer>
    </Modal>
  );
}
